import React, { useMemo, useRef, useEffect } from 'react'
import { useFragment, graphql } from 'react-relay'

import { YStack, XStack, TamaguiElement, StackProps } from 'tamagui'

import { Grid } from '../Grid'
import { GuildCard } from './GuildCard'
import { Spinner } from '../Spinner'

import {
  GuildList_guildsConnection$data,
  GuildList_guildsConnection$key,
} from './__generated__/GuildList_guildsConnection.graphql'

export const GuildList = ({
  guildsConnection,
  itemsPerRow,
  limitNumberOfRows,
  hasNext,
  loadNext,
  ...props
}: {
  guildsConnection: GuildList_guildsConnection$key
  limitNumberOfRows?: number
  itemsPerRow: number
  hasNext: boolean
  loadNext: (count: number) => any
} & StackProps) => {
  const data = useFragment(
    graphql`
      fragment GuildList_guildsConnection on GuildsConnection {
        edges {
          node {
            ...GuildCardNew_guild
          }

          cursor
        }
      }
    `,
    guildsConnection
  )

  const itemGroups = useMemo(() => {
    if (!data.edges) {
      return []
    }

    const groups = data.edges.reduce((memo, item, index) => {
      if (index % itemsPerRow === 0) {
        memo.push([item])
      } else {
        memo[memo.length - 1].push(item)
      }

      return memo
    }, [] as Array<Array<GuildList_guildsConnection$data['edges'][0]>>)

    if (limitNumberOfRows) {
      return groups.slice(0, limitNumberOfRows)
    }

    return groups
  }, [data.edges, itemsPerRow, limitNumberOfRows])

  const endOfListElement = useRef<TamaguiElement>(null)

  useEffect(() => {
    const currentEndOfListElement = endOfListElement.current

    if (
      !currentEndOfListElement ||
      !hasNext ||
      typeof window === 'undefined' ||
      !window.IntersectionObserver
    ) {
      return
    }

    const intersectionObserver = new window.IntersectionObserver((entries) => {
      const someIntersecting = entries.some((entry) => entry.isIntersecting)

      if (someIntersecting) {
        loadNext(Math.max(itemsPerRow * 3, 6))
      }
    })

    intersectionObserver.observe(currentEndOfListElement as unknown as Element)

    return () => {
      intersectionObserver.disconnect()
    }
  }, [endOfListElement.current, data, hasNext, loadNext, itemsPerRow])

  return (
    <YStack {...props} gap='$6'>
      {itemGroups.map((group, index) => {
        return (
          <XStack key={index}>
            <Grid gap='$6'>
              {group.map((edge) => (
                <Grid.Column key={edge.cursor}>
                  <GuildCard
                    guild={edge.node}
                    limitDescriptionLines={8}
                    expand
                  />
                </Grid.Column>
              ))}

              {Array.from({ length: itemsPerRow - group.length }).map(
                (_, index) => (
                  <Grid.Column key={index} />
                )
              )}
            </Grid>
          </XStack>
        )
      })}

      {hasNext ? (
        <Spinner ref={endOfListElement as React.Ref<any>} size='$9' color='$primaryColor' />
      ) : null}
    </YStack>
  )
}
